import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { current_password: "", new_password: "", confirm: "" };
const EMPTY_EMAIL = { new_email: "", current_password: "" };

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [emailForm, setEmailForm] = useState(EMPTY_EMAIL);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => { if (open) setName(user?.name || ""); }, [open, user]);

  const submitName = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Informe o nome");
      return;
    }
    setSavingName(true);
    try {
      const { data } = await api.patch("/auth/me", { name: name.trim() });
      setUser(data);
      toast.success("Nome atualizado com sucesso");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingName(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await api.post("/auth/change-email", {
        new_email: emailForm.new_email,
        current_password: emailForm.current_password,
      });
      toast.success("E-mail alterado com sucesso");
      setEmailForm(EMPTY_EMAIL);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingEmail(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Senha alterada com sucesso");
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-3 pr-6">
          <DialogTitle className="text-lg font-medium">Minha conta</DialogTitle>
          <DialogDescription className="text-sm text-zinc-400">
            Altere seu e-mail de acesso ou sua senha.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submitName} className="flex items-end gap-2" data-testid="change-name-form">
          <div className="flex-1">
            <Label className="text-xs text-zinc-400">Nome</Label>
            <Input data-testid="change-name-input" required value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
          </div>
          <Button data-testid="change-name-submit" type="submit" disabled={savingName || name.trim() === (user?.name || "")}
            className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 rounded-md transition-colors duration-200">
            {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-1">
          <div className="h-px flex-1 bg-zinc-800" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">E-mail</span>
          <div className="h-px flex-1 bg-zinc-800" />
        </div>
        <form onSubmit={submitEmail} className="space-y-4" data-testid="change-email-form">
          <div>
            <Label className="text-xs text-zinc-400">Novo e-mail</Label>
            <Input data-testid="new-email-input" type="email" required value={emailForm.new_email}
              onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
              placeholder="novo@email.com.br"
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Senha atual (confirmação)</Label>
            <Input data-testid="email-password-input" type="password" required value={emailForm.current_password}
              onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
          </div>
          <Button data-testid="change-email-submit" type="submit" disabled={savingEmail}
            className="w-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 rounded-md transition-colors duration-200">
            {savingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar e-mail"}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-1">
          <div className="h-px flex-1 bg-zinc-800" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">Senha</span>
          <div className="h-px flex-1 bg-zinc-800" />
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <Label className="text-xs text-zinc-400">Senha atual</Label>
            <Input data-testid="current-password-input" type="password" required value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Nova senha</Label>
            <div className="relative mt-1.5">
              <Input data-testid="new-password-input" type={show ? "text" : "password"} required minLength={6}
                value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500 pr-10" />
              <button type="button" data-testid="toggle-new-password-visibility" onClick={() => setShow(!show)}
                aria-label={show ? "Ocultar senha" : "Mostrar senha"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors duration-200">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Confirmar nova senha</Label>
            <Input data-testid="confirm-password-input" type="password" required minLength={6} value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
          </div>
          <Button data-testid="change-password-submit" type="submit" disabled={saving}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar nova senha"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
